'use client'

import { useStoreI18n } from '@/store'
import { Accordion, Container, Section, Title } from '@/ui/components'
import IconComponent from '@/ui/Sections/SpotSanityReports/Components/Icon'
import { Tabs } from 'radix-ui'
import { memo } from 'react'

interface Props {
  data: {
    type: 'spotsanity_weather'
    content: {
      title: string
      titleLayout: string
      subtitle: string
      weatherForecasts: {
        poweredBy: string
      }
      items: {
        uid: string
        title: string
        sanityId: string
        coordinates: {
          altitude: string
          hasCoordinates: boolean
          latitude: string
          longitude: string
        }
        days: {
          code: number
          codeDescriptionDay: { text: string }
          codeDescriptionNight: { text: string }
          codeTraditional: number
          date: string
          dateLastUpdated: string
          formattedDate: string
          formattedDateLastUpdated: string
          icon: string
          iconNight: string
          max: string
          min: string
          precipInMm: string
          precipProbabilityPercent: string
          snowFallInCm: string
          snowfallProbabilityPercent: string
          sunshineDurationInHours: string
          sunshineDurationInMinutes: number
          threeHourForecasts: {
            code: number
            windSpeed: string
            precipInMm: string
            hourFrom: number
            temp: string
            windDirDegree: string
            windSpeedMax: string
            precipProbabilityPercent: string
            hourTo: number
            flagNight: number
            icon: string
          }[]
        }[]
      }[]
      tabs: {
        title: string
        uid: string
      }[]
      plugin: {
        type: 'spotsanity_weather'
        view: 'list'
        layout: 'default'
        hasShowMap: boolean
      }
    }
  }
}

const SectionWeather = ({ data }: Props) => {
  const c = data?.content

  // Stores
  const D = useStoreI18n.use.dictionary()

  return (
    <Section data={data} anim="fade-in" className={'s-section-weather pt-xl'}>
      <Container className={'gap-gutter flex justify-between'}>
        {c?.weatherForecasts?.poweredBy && (
          <h5
            className={'h5 [&_a]:underline [&_a]:hover:no-underline'}
            dangerouslySetInnerHTML={{
              __html: c?.weatherForecasts?.poweredBy
            }}
          ></h5>
        )}
      </Container>
      <Tabs.Root defaultValue={'tab-' + c.tabs[0].uid.toString()}>
        <Container className="mb-xs overflow-x-auto pr-0!">
          <Tabs.List className={'gap-2xs flex'} aria-label="Weather">
            {c?.tabs &&
              c?.tabs?.map((tab) => (
                <Tabs.Trigger
                  className={
                    'text-style-capitalize textstyle-md-button py-xs! px-md! data-[state=active]:bg-primary-400 h-auto! max-h-10! cursor-pointer border-0! bg-neutral-200 whitespace-nowrap text-neutral-600 shadow-none! data-[state=active]:text-neutral-100'
                  }
                  key={'tab-' + tab?.uid?.toString()}
                  value={'tab-' + tab?.uid?.toString()}
                >
                  <p className="text-btn text-style-capitalize">{tab.title}</p>
                </Tabs.Trigger>
              ))}
          </Tabs.List>
        </Container>
        <Container className="gap-xl flex flex-col">
          {c?.items?.map((item) => (
            <Tabs.Content
              value={'tab-' + item?.uid?.toString()}
              key={'tab-' + item?.uid?.toString()}
            >
              <div className="gap-xl flex flex-col">
                {item?.days?.map((day, index) => (
                  <div key={day?.date} className="gap-y-xs flex flex-col">
                    <div className="p-lg flex items-center justify-between bg-neutral-200">
                      <div className="">
                        <Title as="h3" className="h3">
                          {day?.formattedDate}
                        </Title>
                        <div className="textstyle-md-regular mt-xs">
                          min. {day?.min} C / max. {day?.max} C
                        </div>
                      </div>
                      <div className="size-12 md:size-14">
                        <IconComponent icon={`weather_big_${day?.icon}`} />
                      </div>
                    </div>

                    <Accordion.Container
                      defaultOpen={
                        index === 0 ? 'info-' + day?.date.toString() : undefined
                      }
                    >
                      <Accordion.Item
                        contentClassName="pb-0!"
                        value={'info-' + day?.date.toString()}
                        head={
                          <>
                            <span className="h4 group-data-[state=closed]/accordion-item:hidden">
                              {D['fe.weather.close.details']}
                            </span>
                            <span className="h4 group-data-[state=open]/accordion-item:hidden">
                              {D['fe.weather.more.details']}
                            </span>
                          </>
                        }
                      >
                        {day?.threeHourForecasts?.map((forecast) => (
                          <div
                            key={forecast?.hourFrom}
                            className="py-md gap-x-md flex items-center justify-between border-t border-neutral-300 md:grid md:grid-cols-3"
                          >
                            <div className="textstyle-md-bold text-left">
                              {forecast?.hourFrom.toString().padStart(2, '0')}{' '}
                              {D['fe.date.hour']}
                            </div>
                            <div className="text-center">
                              <div className="gap-x-xs md:gap-x-md inline-flex items-center">
                                <div className="flex items-center justify-center">
                                  <IconComponent
                                    width={40}
                                    height={40}
                                    className="size-8 md:size-full"
                                    icon={`weather_big_${forecast?.icon}`}
                                  />
                                </div>
                                <div>
                                  <Title
                                    as="h4"
                                    className="md:h4 max-md:textstyle-md-bold text-left"
                                  >
                                    {forecast?.temp}
                                  </Title>
                                  <div className="textstyle-sm-regular text-left leading-0 text-neutral-400">
                                    {D['fe.weather.celsius']}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="inline-flex items-center">
                                <IconComponent
                                  className="size-8 md:size-12 [&_path]:fill-neutral-400"
                                  icon="weather_peak_windspeed"
                                />
                                <div className="md:pl-md inline-flex flex-col text-left">
                                  <div className="gap-x-2xs inline-flex">
                                    <Title
                                      as="h4"
                                      className="md:h4 max-md:textstyle-md-bold"
                                    >
                                      {forecast?.windSpeed}
                                    </Title>
                                    <div
                                      className="inline-flex size-5 origin-center items-center md:size-7"
                                      style={{
                                        transform: `rotate(${forecast?.windDirDegree}deg)`
                                      }}
                                    >
                                      <IconComponent
                                        className="size-4.5 md:size-6.5"
                                        icon="weather_arrow"
                                      />
                                    </div>
                                  </div>
                                  <span className="textstyle-sm-regular text-neutral-400">
                                    km/h
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </Accordion.Item>
                    </Accordion.Container>
                  </div>
                ))}
              </div>
              {/* <CardWeather selectedDate={selectedTab} data={item} />
                  <CardWeatherForecast
                    selectedDate={selectedTab}
                    data={data?.stations?.[0]}
                    stations={c?.subStation}
                  /> */}
            </Tabs.Content>
          ))}
        </Container>
      </Tabs.Root>
    </Section>
  )
}

export default memo(SectionWeather)
